import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import * as echarts from "echarts";

@Component({
    name: 'bi-chart-component',
})
export default class BiChartComponent extends Vue {

    @Prop({type: String, default: 'book', required: true}) type!: string;
    @Prop({type: Array<any>, required: true}) data!: any;
    @Prop({type: Number }) selMonth!: number;

    get cleansedData(){
        let tData = JSON.parse(JSON.stringify(this.data));
        return tData.map((item: any) => {
            if(item['ACT'] != null && item['ACT'] == '0'){
                return {
                    ...item,
                    'ACT': null
                }
            }

            return item;
        });
    }

    load() {
        this.loadChart();
    }

    loadChart() {
        let chartDom = document.getElementById('bi-chart');
        let biChart = echarts.init(chartDom);
        let option = this.getChartOption();
        biChart.setOption(option);
    }

    reload() {
        let chartDom = document.getElementById('bi-chart');
        let biChart = echarts.init(chartDom);
        let option = this.getChartOption();
        biChart.resize();
        biChart.setOption(option, true);
    }

    private getChartOption() {
        const titleMap: Record<string, string> = {
            book: this.$t('Daily_Book_Status').toString(),
            sales: this.$t('Daily_Sales_Status').toString(),
            inventory: this.$t('Daily_Inventory_Status').toString()
        };

        const yAxisMap: Record<string, echarts.YAXisComponentOption[]> = {
            book: [
                {position: 'left', type: 'value', interval: 50, min: 0},
                {position: 'right', type: 'value', interval: 200},
            ],
            sales: [
                {position: 'left', type: 'value', interval: 50, min: 0},
                {
                    position: 'right',
                    type: 'value',
                    interval: 20,
                    axisLabel: {formatter: `{value} %`},
                    min: 0,
                    max: 100,
                },
            ],
            inventory: [
                {position: 'left', type: 'value', interval: 100, min: 0},
                {position: 'right', type: 'value', interval: 100},
            ]
        };

        const seriesMap: Record<string, echarts.SeriesOption[]> = {
            book: this.getBookSeries(),
            sales: this.getSalesSeries(),
            inventory: this.getInventorySeries()
        };

        const xAxis: echarts.XAXisComponentOption[] = [
            {type: 'category', maxInterval: 1, min: 0, max: 31},
        ];

        const option: powerbi.EChartsOption = {
            title: {text: titleMap[this.type] || '', show: false},
            legend: {},
            tooltip: {trigger: 'axis'},
            xAxis,
            yAxis: yAxisMap[this.type] || [],
            dataset: {id: 'id_' + this.type, source: this.cleansedData},
            series: seriesMap[this.type] || [],
            grid: {left: '5%', right: '5%', bottom: '10%', containLabel: true},
            dataZoom: [
                {type: 'slider', start: this.type == 'inventory' ? 70: 100, end: 0},
                {type: 'inside', start: 0, end: 20},
            ],
        };

        console.debug('chart option', option);
        return option;
    }

    private getBookSeries(): echarts.SeriesOption[] {

        return [
            this.createSeries('ACT', 'line', '#FF0000'),
            {
                symbolSize: 0,
                ...this.createSeries('BGT', 'line', '#66CCFF'),
            } as echarts.SeriesOption,
            {
                symbolSize: 0,
                ...this.createSeries('PLN','line', '#66CC00')
            } as echarts.SeriesOption,
            this.createSeries('BACKLOG','line', '#6666CC', 1),
        ];
    }

    private getSalesSeries(): echarts.SeriesOption[] {
        const todayIndex = this.getTodayIndex();

        return [
            this.createSeries('ACT', 'line', '#FF0000'),
            {
                symbolSize: 0,
                ...this.createSeries('BGT','line', '#66CCFF')
            } as echarts.SeriesOption,
            {
                symbolSize: 0,
                ...this.createSeries('PLN','line', '#66CC00')
            } as echarts.SeriesOption,
            { type: 'scatter', label: { show: true, position: 'top', formatter: this.getLastValueFormatterByDay(todayIndex, 'ACT GP') }, color: '#993333', yAxisIndex: 1 },
            this.createSeries('PLN GP','line', '#6666CC'),
        ];
    }

    private getInventorySeries(): echarts.SeriesOption[] {

        return [
            {type: 'line', label: {show: true, formatter: this.getLastValueFormatter('ACT')}, color: '#FF0000'},
            {type: 'line', symbolSize: 0, color: '#66CCFF', yAxisIndex: 1},
            {type: 'line', symbolSize: 0, color: '#66CC00', yAxisIndex: 1},
            {type: 'bar', label: {show: true, formatter: this.getLastValueFormatter('ACT-Storage')}, stack: 'stack', color: '#CC9999'},
            {type: 'bar', label: {show: true, formatter: this.getLastValueFormatter('ACT-PJT')}, stack: 'stack', color: '#FFCCCC'},
        ];
    }

    private getTodayIndex() {
        const today = new Date();
        if(today.getMonth() + 1 == this.selMonth){
            return today.getDate() > 1 ? today.getDate() - 2 : today.getDate() - 1;
        }
        else{
            let lastDay = new Date(today.getFullYear(), this.selMonth, 0);
            return lastDay.getDate() - 1;
        }
    }

    private createSeries(
        key: string,
        type: 'line' | 'bar' | 'scatter',
        color: string,
        yAxisIndex: number = 0,
    ): echarts.SeriesOption {
        const todayIndex = this.getTodayIndex();

        return {
            type: type,
            label: {
                show: true,
                formatter: this.getLastValueFormatterByDay(todayIndex, key),
            },
            color: color,
            yAxisIndex: yAxisIndex,
        };
    }

    private getLastValueFormatterByDay(todayIndex: number, key: string): string | echarts.LabelFormatterCallback<echarts.DefaultLabelFormatterCallbackParams> | undefined {
        return (params) => {
            // 값 없는 경우 표시 안함. 마지막 일자만 표시, 오늘자 데이터는 없고 전날 데이터 까지만 표기
            if (params.value == null || params.dataIndex !== todayIndex) {
                return '';
            }
            return (params.value as any)[key] != 0 ? (params.value as any)[key].toString() : '';
        };
    }

    private getLastValueFormatter(key: string): string | echarts.LabelFormatterCallback<echarts.DefaultLabelFormatterCallbackParams> | undefined {
        let lastIndex = this.data.filter((item: any) => {
            return item[key] != 0;
        }).length - 1;
        return (params) => {
            // 값 없는 경우 표시 안함. 마지막 데이터만 표시
            if (params.value == null || (params.value as any)[key] == 0 || params.dataIndex !== lastIndex) {
                return '';
            }
            return (params.value as any)[key] != 0 ? (params.value as any)[key].toString() : '';
        };
    }

    @Watch('data')
    onDataChanged(newValue: any, oldValue: any) {
        if (oldValue == null) {
            this.load();
        } else {
            this.reload();
        }
    }

    mounted() {
        if (this.data != null) {
            this.reload();
        }
    }
}